/**
 * Storico.
 *
 * Ogni azione con il suo stato, la sua ricevuta e — finché è possibile — il
 * tasto per annullarla. È il registro che rende verificabile tutto il resto:
 * se l'host vuole sapere cosa è successo alle 14:32, qui lo trova.
 */

import { useMemo, useState } from "react";
import { History as HistoryIcon } from "lucide-react";
import { CATEGORY_LABELS, type ActionView } from "@otto/shared";
import { formatWhen } from "../lib/quando.ts";
import { useStore } from "../lib/store.tsx";
import {
  Badge,
  Card,
  EmptyState,
  Pill,
  StatusDot,
  Thumb,
  cx,
} from "../ui/primitives.tsx";
import { Sheet } from "../ui/Sheet.tsx";
import { ProposalCard } from "../blocks/ProposalCard.tsx";

type Filter = "tutte" | "da_confermare" | "fatte" | "non_riuscite";

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "tutte", label: "Tutte" },
  { id: "da_confermare", label: "Da confermare" },
  { id: "fatte", label: "Fatte" },
  { id: "non_riuscite", label: "Non riuscite" },
];

export function History() {
  const store = useStore();
  const [filter, setFilter] = useState<Filter>("tutte");
  const [selected, setSelected] = useState<ActionView | null>(null);

  const actions = useMemo(() => {
    const all = [...store.actions.values()].sort((a, b) =>
      b.proposedAt.localeCompare(a.proposedAt),
    );
    switch (filter) {
      case "da_confermare":
        return all.filter((action) => action.status === "proposta");
      case "fatte":
        return all.filter(
          (action) =>
            action.status === "eseguita" || action.status === "rollback",
        );
      case "non_riuscite":
        return all.filter((action) => action.status === "fallita");
      default:
        return all;
    }
  }, [store.actions, filter]);

  return (
    <>
      <div className="scroll-soft -mx-1 -mt-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1 pt-1">
        {FILTERS.map((option) => (
          <Pill
            key={option.id}
            size="sm"
            variant={filter === option.id ? "primary" : "secondary"}
            onClick={() => setFilter(option.id)}
          >
            {option.label}
          </Pill>
        ))}
      </div>

      {actions.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon size={28} />}
          title="Ancora niente qui"
          hint="Ogni modifica che approvi finisce in questo elenco, con la ricevuta di quando è arrivata sui canali."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {actions.map((action) => (
            <ActionRow
              key={action.id}
              action={action}
              onOpen={() => setSelected(action)}
            />
          ))}
        </div>
      )}

      <Sheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title="Dettaglio"
      >
        {selected ? (
          <div className="pt-1">
            <ProposalCard actionId={selected.id} fallback={selected.preview} />
            {selected.intentText ? (
              <p className="mt-3 px-1 text-[13px] leading-relaxed text-ink-faint">
                Da: «{selected.intentText}»
              </p>
            ) : null}
          </div>
        ) : null}
      </Sheet>
    </>
  );
}

function ActionRow({
  action,
  onOpen,
}: {
  action: ActionView;
  onOpen: () => void;
}) {
  const state = STATES[action.status];
  const target = action.preview.targets[0];

  return (
    <Card interactive onClick={onOpen} className="flex items-start gap-3 p-3">
      <StatusDot tone={state.tone} className="mt-1.5" />
      {target ? (
        <Thumb src={target.photoUrl} alt={target.nickname} size={38} />
      ) : null}

      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-ink">
          {action.preview.summary}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cx("text-[12px] font-semibold", state.className)}>
            {state.label}
          </span>
          <span className="text-[12px] text-ink-faint">
            {formatWhen(action.executedAt ?? action.proposedAt, "breve")}
          </span>
          <Badge>{CATEGORY_LABELS[action.category]}</Badge>
        </div>
      </div>
    </Card>
  );
}

const STATES: Record<
  ActionView["status"],
  { tone: "ok" | "wait" | "danger" | "idle"; label: string; className: string }
> = {
  proposta: {
    tone: "idle",
    label: "Da confermare",
    className: "text-ink-faint",
  },
  approvata: { tone: "wait", label: "In corso", className: "text-ink-soft" },
  in_esecuzione: {
    tone: "wait",
    label: "In corso",
    className: "text-ink-soft",
  },
  eseguita: { tone: "ok", label: "Fatta", className: "text-ok" },
  fallita: { tone: "danger", label: "Non riuscita", className: "text-danger" },
  annullata: { tone: "idle", label: "Rifiutata", className: "text-ink-faint" },
  rollback: { tone: "idle", label: "Annullata", className: "text-ink-faint" },
};
